<script setup lang="ts">
  import { useGetFeatureFlags } from "@/api/config/config"
  import { ExternalRoute } from "@/constants/externalRoute"
  import { EnrolmentRoute } from "@/constants/routes/enrolment"
  import { useStepWizard } from "@/lib/useStepWizard"
  import { formatCurrency } from "@/lib/utils"
  import { computed, onMounted, ref, type ComponentPublicInstance } from "vue"
  import { useRoute } from "vue-router"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import { StepWizard } from "@/components/ui/step-wizard"

  import { BrokerCookieName } from "@/views/enrolment/brokerConstants"
  import { trackEnrolment } from "@/views/enrolment/enrolmentTracking"
  import {
    getBrokerCookie,
    getBrokerRef,
    getPaidClick,
    processBrokerQueryParams,
    processReferralQueryParam,
  } from "@/views/enrolment/useBroker"
  import { useEnrolment } from "@/views/enrolment/useEnrolment"
  import { useEnrolmentAffiliateTracking } from "@/views/enrolment/useEnrolmentAffiliate"

  const route = useRoute()
  const { modelValue, goBack } = useStepWizard()
  const { extras, resetEnrolment, updateExtras, updateAddress } = useEnrolment()
  useEnrolmentAffiliateTracking()

  const featureFlagsQuery = useGetFeatureFlags()

  const isEvBrokerEntry = ref(false)
  const isFirstStep = computed(() => modelValue.value === 0)

  const showEvUnavailableAlert = computed(
    () =>
      isFirstStep.value &&
      isEvBrokerEntry.value &&
      featureFlagsQuery.data.value?.evTariffOfferInAcquisition === false,
  )

  const currentStepRef = ref<ComponentPublicInstance | null>(null)

  const referral = computed(() => extras.value.referral)
  const referralError = computed(() => extras.value.referralError)
  const showReferralError = ref(true)
  const referralReward = computed(() =>
    referral.value
      ? formatCurrency(referral.value.currentReferralFee, {
          hideDecimals: true,
        })
      : "",
  )

  const handleBack = () => {
    const step = currentStepRef.value as {
      tryRegressEvSubStep?: () => boolean
    } | null
    if (step?.tryRegressEvSubStep?.()) return
    goBack()
  }

  onMounted(async () => {
    resetEnrolment()

    const query = route.query

    isEvBrokerEntry.value = query.type === "ev"

    const postcodeParam = query.postalcode
    if (postcodeParam) {
      const formattedPostcode = String(postcodeParam).trim().toUpperCase()
      updateAddress({ postcode: formattedPostcode })
    }

    processBrokerQueryParams(query)

    const referralResult = await processReferralQueryParam(query)
    if (referralResult?.status === "valid") {
      updateExtras({
        voucherCode: referralResult.voucherCode,
        referral: {
          referrer: referralResult.referrer,
          currentReferralFee: referralResult.currentReferralFee,
        },
      })
      trackEnrolment("voucherDetailsAppliedToQuoteProcess")
    } else if (referralResult?.status === "invalid") {
      updateExtras({ referralError: true })
    }

    const brokerCookie = getBrokerCookie()
    const paidClick = brokerCookie ? undefined : getPaidClick()
    updateExtras({
      brokerCookie:
        brokerCookie ??
        (paidClick && { name: paidClick.broker, value: paidClick.broker }),
      campaignReference: paidClick?.campaignReference,
      thirdPartySwitchReference: getBrokerRef(),
    })

    if (
      referralResult?.status !== "valid" &&
      brokerCookie &&
      brokerCookie.name === BrokerCookieName.voucher
    ) {
      updateExtras({ voucherCode: brokerCookie.value })
    }
  })

  const steps = [
    { route: EnrolmentRoute.enrolmentAddress },
    { route: EnrolmentRoute.enrolmentProperty },
    { route: EnrolmentRoute.enrolmentTariff },
    { route: EnrolmentRoute.enrolmentPersonalDetails },
    { route: EnrolmentRoute.enrolmentPayment },
    { route: EnrolmentRoute.enrolmentReview },
  ]

  const tariffStepIndex = steps.findIndex((s) =>
    s.route.includes(EnrolmentRoute.enrolmentTariff),
  )
  const showBackButton = computed(() => modelValue.value === tariffStepIndex)

  const onLeave = () => {
    window.location.assign(ExternalRoute.websiteHome)
  }
</script>

<template>
  <StepWizard
    :steps="steps"
    title="Get a Quote"
    :show-back-button="showBackButton"
    @leave-page="onLeave"
    @back="handleBack"
  >
    <Alert
      v-if="showEvUnavailableAlert"
      variant="warning"
      title="Our So EV tariff is temporarily unavailable"
      class="mx-auto mb-4 sm:mb-6"
      data-testid="ev-unavailable-alert"
    >
      <AlertDescription>
        We've temporarily removed the So EV tariff from sale while we review
        pricing. Existing So EV customers aren't affected.
      </AlertDescription>
    </Alert>

    <Alert
      v-if="referralError && showReferralError"
      variant="error"
      show-close-button
      class="mb-4 sm:mb-6"
      data-testid="referral-error-alert"
      @close-button-clicked="showReferralError = false"
    >
      <AlertDescription>
        That referral code wasn't recognised, so the £50 credit won't be
        applied. You can still continue your quote.
      </AlertDescription>
    </Alert>

    <Alert
      v-if="referral"
      variant="success"
      class="mb-4 sm:mb-6"
      data-testid="referral-alert"
    >
      <AlertDescription>
        {{ referral.referrer }} has referred you — complete your switch to So
        Energy and get {{ referralReward }} credit.
      </AlertDescription>
    </Alert>
    <router-view v-slot="{ Component }">
      <keep-alive>
        <component :is="Component" :key="$route.path" ref="currentStepRef" />
      </keep-alive>
    </router-view>
  </StepWizard>
</template>
